import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { Zap, LogIn, UserPlus, Building2 } from 'lucide-react';

export default function LoginPage() {
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const [isLogin, setIsLogin] = useState(true);
  const [form, setForm] = useState({ name: '', email: '', password: '', role: 'mitarbeiter' });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (isLogin) {
        await login(form.email, form.password);
        toast.success('Erfolgreich angemeldet');
      } else {
        await register(form.name, form.email, form.password, form.role);
        toast.success('Konto erstellt');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Fehler bei der Anmeldung');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 flex items-center justify-center p-4" data-testid="login-page">
      <div className="w-full max-w-md">
        <div className="text-center mb-8 animate-fade-in">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-sm bg-amber-500 mb-4 shadow-[0_0_30px_rgba(245,158,11,0.3)]">
            <Zap className="w-8 h-8 text-slate-950" strokeWidth={2.5} />
          </div>
          <h1 className="text-4xl font-black text-white tracking-tight" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>Mont<span className="text-amber-500">ARO</span></h1>
          <p className="text-slate-400 mt-2 text-xs font-mono uppercase tracking-wider">Automatisiert. Rechnet ab. Schafft Ordnung.</p>
        </div>

        <div className="bg-slate-900/50 border border-slate-800/60 backdrop-blur-md rounded-sm p-6 animate-slide-up">
          <div className="flex mb-6 bg-slate-950 rounded-sm p-1">
            <button onClick={() => setIsLogin(true)} data-testid="login-tab"
              className={`flex-1 py-2 px-4 rounded-sm text-sm font-medium transition-all ${isLogin ? 'bg-amber-500 text-slate-950' : 'text-slate-400 hover:text-white'}`}>
              <LogIn className="w-4 h-4 inline mr-2" />Anmelden
            </button>
            <button onClick={() => setIsLogin(false)} data-testid="register-tab"
              className={`flex-1 py-2 px-4 rounded-sm text-sm font-medium transition-all ${!isLogin ? 'bg-amber-500 text-slate-950' : 'text-slate-400 hover:text-white'}`}>
              <UserPlus className="w-4 h-4 inline mr-2" />Registrieren
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {!isLogin && (
              <div>
                <Label className="text-slate-300 text-sm">Name</Label>
                <Input data-testid="name-input" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })}
                  placeholder="Max Mustermann" required={!isLogin}
                  className="bg-slate-950 border-slate-800 focus:border-amber-500 focus:ring-1 focus:ring-amber-500 text-slate-200 placeholder:text-slate-600 mt-1" />
              </div>
            )}
            <div>
              <Label className="text-slate-300 text-sm">E-Mail</Label>
              <Input data-testid="email-input" type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })}
                placeholder="max@beispiel.de" required
                className="bg-slate-950 border-slate-800 focus:border-amber-500 focus:ring-1 focus:ring-amber-500 text-slate-200 placeholder:text-slate-600 mt-1" />
            </div>
            <div>
              <Label className="text-slate-300 text-sm">Passwort</Label>
              <Input data-testid="password-input" type="password" value={form.password} onChange={e => setForm({ ...form, password: e.target.value })}
                placeholder="Mindestens 6 Zeichen" required
                className="bg-slate-950 border-slate-800 focus:border-amber-500 focus:ring-1 focus:ring-amber-500 text-slate-200 placeholder:text-slate-600 mt-1" />
            </div>
            {!isLogin && (
              <div>
                <Label className="text-slate-300 text-sm">Rolle</Label>
                <Select value={form.role} onValueChange={v => setForm({ ...form, role: v })}>
                  <SelectTrigger data-testid="role-select" className="bg-slate-950 border-slate-800 text-slate-200 mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-900 border-slate-800">
                    <SelectItem value="mitarbeiter" className="text-slate-200">Mitarbeiter</SelectItem>
                    <SelectItem value="projektleiter" className="text-slate-200">Projektleiter</SelectItem>
                    <SelectItem value="admin" className="text-slate-200">Administrator</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
            <Button data-testid="submit-button" type="submit" disabled={loading}
              className="w-full bg-amber-500 text-slate-950 hover:bg-amber-400 font-bold rounded-sm shadow-[0_0_15px_rgba(245,158,11,0.3)] transition-all active:scale-95">
              {loading ? 'Laden...' : (isLogin ? 'Anmelden' : 'Registrieren')}
            </Button>
          </form>

          <div className="mt-4 pt-4 border-t border-slate-800/40 text-center">
            <p className="text-xs text-slate-500 mb-2">Noch keine Firma registriert?</p>
            <Button variant="ghost" onClick={() => navigate('/register')} data-testid="go-register-company"
              className="text-amber-400 hover:text-amber-300 hover:bg-amber-500/10 text-sm">
              <Building2 className="w-4 h-4 mr-2" />Neue Firma registrieren
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
